// frontend/src/pages/admin/TicketStatusForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Checkbox, Space } from 'antd';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext'; // NEW: Import notification API

const TicketStatusForm = ({ visible, onCancel, onSuccess, ticketStatus }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const notificationApi = useNotificationApi(); // NEW: Get notification API from context

  // Helper functions for notifications (standardized)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300; // Delay before showing notification
    const minDisplayTime = 500; // Minimum display time once shown

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0, // Will be dismissed manually
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          // If dismissNotification is a function, call it, otherwise destroy by key
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          // If notification was never shown (e.g., operation completed too fast), just ensure it's destroyed.
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Set initial form values when modal becomes visible or ticketStatus changes
  useEffect(() => {
    if (visible) {
      if (ticketStatus) {
        // Editing existing status: Populate form with existing data
        form.setFieldsValue({
          name: ticketStatus.name,
          // 'isFinal' is already a boolean from mapTicketStatusToCamelCase on backend response
          isFinal: ticketStatus.isFinal,
        });
      } else {
        // Adding new status: Clear form and set default values
        form.resetFields(); // Clear previous values
        form.setFieldsValue({ isFinal: false }); // Default for new status: not final
      }
    }
  }, [visible, ticketStatus, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    const dismissLoading = showLoading('ticketStatusSubmit', ticketStatus ? 'Updating status...' : 'Creating status...');
    try {
      if (ticketStatus) {
        // Update existing ticket status
        // Frontend sends 'name' and 'isFinal' (camelCase)
        await api.put(`/api/ticket-statuses/${ticketStatus.id}`, values);
        showSuccess('Update Successful', `Ticket status "${values.name}" updated successfully.`);
      } else {
        // Create new ticket status
        // Frontend sends 'name' and 'isFinal' (camelCase)
        await api.post('/api/ticket-statuses', values);
        showSuccess('Creation Successful', `New ticket status "${values.name}" created successfully.`);
      }
      onSuccess(); // Call onSuccess callback to refresh table
      form.resetFields(); // Reset form after successful submission
    } catch (error) {
      console.error('Failed to save ticket status:', error);
      const errorMessage = error.response?.data?.message || 'Error saving ticket status.';
      const errorDetails = process.env.NODE_ENV === 'development' ? error.response?.data?.error : null;
      showError(
        'Operation Failed',
        <span>
          {errorMessage}
          {errorDetails && <div>Details: {errorDetails}</div>}
        </span>,
        'ticketStatusSubmitError'
      );
    } finally {
      setLoading(false);
      dismissLoading();
    }
  };

  return (
    <Modal
      visible={visible}
      title={ticketStatus ? 'Edit Ticket Status' : 'Add New Ticket Status'}
      onCancel={onCancel}
      footer={[
        <Space key="footer-buttons">
          <Button key="back" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()} // Trigger form submission
          >
            {ticketStatus ? 'Update Status' : 'Add Status'}
          </Button>
        </Space>,
      ]}
      width={600} // Standard width for forms similar to other standard forms
      destroyOnClose={true} // Ensures form state is reset when modal closes
    >
      <Form
        form={form}
        layout="vertical"
        name="ticket_status_form"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="name" // Frontend uses camelCase 'name'
          label="Status Name"
          rules={[
            { required: true, message: 'Please input the status name!' },
            { type: 'string', min: 2, message: 'Status name must be at least 2 characters.' },
            { max: 100, message: 'Status name cannot exceed 100 characters.' },
          ]}
        >
          <Input placeholder="e.g., Open, In Progress, Resolved" />
        </Form.Item>

        <Form.Item
          name="isFinal" // Frontend uses camelCase 'isFinal'
          valuePropName="checked" // Essential for Checkbox to work correctly with boolean values
          initialValue={false} // Default for new status: false (not final)
        >
          <Checkbox>Is this a final status (e.g., Resolved, Closed)?</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TicketStatusForm;